import React from 'react';
import { Download, Loader2, Trash2, Clapperboard, AlertTriangle } from 'lucide-react';
import { fileUrl } from '../../api';

const HOWTO = {
  portrait: 'Save to your phone, then open Instagram (Reels/Story), TikTok or YouTube Shorts and upload.',
  square: 'Post straight to your Instagram or Facebook feed.',
  landscape: 'Upload to YouTube, or attach to a post on X or LinkedIn.',
};

export default function LaunchVideos({ videos, onDelete }) {
  if (!videos.length) return null;
  return (
    <div className="space-y-4" data-testid="launch-videos">
      {videos.map((v) => (
        <div key={v.id} className="rounded-2xl border hairline p-4" data-testid={`launch-video-${v.id}`}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="flex items-center gap-2 text-sm text-cream"><Clapperboard size={15} className="text-gold" /> {v.title}</p>
            <div className="flex items-center gap-3">
              {v.status === 'rendering' && <span className="flex items-center gap-2 text-xs text-gold" data-testid={`launch-status-${v.id}`}><Loader2 size={13} className="animate-spin" /> Producing… {v.kind === 'ai' && v.progress ? v.progress : `${Object.keys(v.formats || {}).length}/3 formats`}</span>}
              {v.status === 'ready' && <span className="text-[10px] tracking-widest uppercase text-emerald-400 border border-emerald-500/40 rounded-full px-2 py-0.5" data-testid={`launch-status-${v.id}`}>Ready</span>}
              {v.status === 'failed' && <span className="flex items-center gap-1 text-xs text-heart" data-testid={`launch-status-${v.id}`}><AlertTriangle size={13} /> Something went wrong — try again</span>}
              <button onClick={() => onDelete(v.id)} data-testid={`launch-delete-${v.id}`} className="text-cream/35 hover:text-heart transition-colors"><Trash2 size={14} /></button>
            </div>
          </div>
          {Object.keys(v.formats || {}).length > 0 && (
            <div className="mt-4 grid sm:grid-cols-3 gap-3">
              {['portrait', 'square', 'landscape'].map((fmt) => {
                const f = v.formats?.[fmt];
                if (!f) return <div key={fmt} className="rounded-xl border hairline border-dashed p-3 text-xs text-cream/35 grid place-items-center min-h-[120px]"><Loader2 size={14} className="animate-spin" /></div>;
                const src = fileUrl(f.url);
                return (
                  <div key={fmt} className="rounded-xl border hairline p-3" data-testid={`launch-format-${fmt}`}>
                    <video src={src} poster={f.poster ? fileUrl(f.poster) : undefined} controls preload="metadata" className={`w-full rounded-lg bg-black ${fmt === 'portrait' ? 'aspect-[9/16] max-h-56 mx-auto' : fmt === 'square' ? 'aspect-square' : 'aspect-video'}`} />
                    <p className="text-xs text-cream mt-2">{f.label}</p>
                    <p className="text-[11px] text-cream/45 mt-1 leading-snug">{HOWTO[fmt]}</p>
                    <a href={`${src}?download=1&name=${encodeURIComponent(`${v.title} (${fmt})`)}`} download data-testid={`launch-download-${fmt}`} className="mt-2 inline-flex items-center gap-1.5 text-xs text-gold hover:underline"><Download size={12} /> Download MP4</a>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
